// App-wide state. `doc` is what the user sees: the server's acknowledged document (`serverDoc`)
// with this device's pending edits applied on top.

import { computed, signal } from "@preact/signals";
import { DocIndex } from "../format/derive.ts";
import { applyOp } from "../format/ops.ts";
import type { Anchor, Cue, Doc, Moment, Note, NoteStyle, Standby } from "../format/types.ts";
import type { PendingEntry, Proposal, Role, ShowState } from "./idb.ts";

function makeTabId(): string {
  const existing = sessionStorage.getItem("q2q-tab");
  if (existing) return existing;
  const bytes = crypto.getRandomValues(new Uint8Array(6));
  const id = `t-${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
  sessionStorage.setItem("q2q-tab", id);
  return id;
}

/** This tab's id. Survives a reload of the same tab (sessionStorage), so its pending edits stay its own. */
export const tabId = makeTabId();

export const serverDoc = signal<Doc | null>(null);
/** Edits made on this device and not yet acknowledged, oldest first. */
export const pending = signal<PendingEntry[]>([]);

export const doc = computed<Doc | null>(() => {
  const base = serverDoc.value;
  if (!base) return null;
  if (!pending.value.some((e) => !e.held)) return base;
  const d = structuredClone(base);
  // Work kept through sign-out is reviewed before it's shown or sent again.
  for (const e of pending.value) if (!e.held) for (const op of e.ops) applyOp(d, op);
  return d;
});

export const ix = computed(() => (doc.value ? new DocIndex(doc.value) : null));

/** The open show as the server last acknowledged it, and what this account may do with it. */
export interface ShowInfo {
  accountId: string;
  showId: string;
  title: string;
  revision: number;
  generation: number;
  state: ShowState;
  role: Role;
  sourceSha256: string;
  /** Why editing is off right now (archived, viewer, access removed…), or "" when it's on. */
  lock: string;
}
export const show = signal<ShowInfo>({ accountId: "", showId: "", title: "", revision: 0, generation: 0, state: "active", role: "viewer", sourceSha256: "", lock: "" });

/** Edits the server refused, kept for explicit review. */
export const proposals = signal<Proposal[]>([]);

/** Other people (and this person's other tabs) connected to the show right now. */
export interface Person {
  userId: string;
  name: string;
  role: Role;
  tab: string;
  page: number;
  editing: string | null;
}
export const presence = signal<Person[]>([]);

export type SaveStatus =
  | { kind: "saved" }
  | { kind: "saving" }
  | { kind: "offline"; message: string }
  | { kind: "error"; message: string; problems: string[] };
export const status = signal<SaveStatus>({ kind: "saved" });
export const environment = signal<"local" | "staging" | "production">("production");

// ---------------------------------------------------------------- view settings (this device only)

export interface ViewSettings {
  hiddenDepartments: string[];
  hiddenLayers: string[];
  /** Show the page as the prompt book export draws it (labels in the margin), instead of edit marks. */
  preview: boolean;
  scale: string;
  page: number;
  /** Mouse only; Pencil and finger input keep their own interaction rules. */
  mouseMode: "interact" | "place";
  /** Preferred annotation/editor lane width in CSS pixels; clamped to the current window. */
  gutterWidth: number;
}
const DEFAULT_VIEW: ViewSettings = { hiddenDepartments: [], hiddenLayers: [], preview: false, scale: "page-width", page: 1, mouseMode: "interact", gutterWidth: 240 };
export const view = signal<ViewSettings>(DEFAULT_VIEW);
/** The departments used for the last new cue, preselected next time. */
export const lastDepartments = signal<string[]>([]);

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...fallback, ...(JSON.parse(raw) as T) } : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Load this device's settings for one account and show. Filters, page and zoom belong to the
 * show; the mouse tool and lane width are this account's preferences on this browser.
 */
export function scopeSettings(accountId: string, showId: string): void {
  const showKey = `q2q-view.${accountId}.${showId}`;
  const prefsKey = `q2q-prefs.${accountId}`;
  const prefs = readJson(prefsKey, { mouseMode: DEFAULT_VIEW.mouseMode, gutterWidth: DEFAULT_VIEW.gutterWidth });
  view.value = { ...readJson(showKey, DEFAULT_VIEW), mouseMode: prefs.mouseMode, gutterWidth: prefs.gutterWidth };
  const deptKey = `q2q-last-depts.${accountId}.${showId}`;
  try {
    lastDepartments.value = JSON.parse(localStorage.getItem(deptKey) ?? "[]") as string[];
  } catch {
    lastDepartments.value = [];
  }
  view.subscribe((v) => {
    const { mouseMode, gutterWidth, ...rest } = v;
    localStorage.setItem(showKey, JSON.stringify(rest));
    localStorage.setItem(prefsKey, JSON.stringify({ mouseMode, gutterWidth }));
  });
  lastDepartments.subscribe((v) => localStorage.setItem(deptKey, JSON.stringify(v)));
}

// ---------------------------------------------------------------- editing UI

export type MarkKind = "moment" | "standby" | "note";
export interface MarkRef {
  kind: MarkKind;
  id: string;
}
export const selected = signal<MarkRef | null>(null);

/** Transient arrange selection; an empty set still means Select cues mode is active. */
export const arrangeSelection = signal<{ page: number | null; ids: string[] } | null>(null);

export interface CueRowForm {
  department: string;
  cueId: string | null;
  number: string;
  label: string;
  notes: string;
}

/** The open editor. It is mirrored to IndexedDB while open, so a reload restores it. */
export interface PanelForm {
  mode: "new" | "edit";
  kind: "cue" | "standby" | "note";
  anchor: Anchor;
  /** The moment, standby or note being edited (or the id a new one will get). */
  id: string;
  rows: CueRowForm[];
  scriptText: string;
  layer: string | null;
  newLayerName: string;
  style: NoteStyle;
  placement: "margin" | "inside";
  text: string;
  error: string | null;
  restored: boolean;
  /** A standby's last cue, as picked in the editor; "" means "use the suggestion". */
  through: string;
  /** The records as they were when the editor opened: what the save's ops are based on. */
  originals: { moment?: Moment; cues: Cue[]; standby?: Standby; note?: Note };
}
export const panel = signal<PanelForm | null>(null);
/** Keep the editor inert until its closed draft has been durably removed. */
export const closingPanel = signal(false);

export type Dialog = "departments" | "layers" | "project" | "export" | "menu" | "status" | "members" | "history" | "proposals" | "show" | "shortcuts" | null;
export const dialog = signal<Dialog>(null);
export const cueListOpen = signal(false);
export const cueListView = signal<"cues" | "notes">("cues");

export interface Notice {
  id: number;
  text: string;
  tone: "info" | "warn" | "error";
  action?: { label: string; run: () => void };
}
export const notices = signal<Notice[]>([]);
let noticeSeq = 0;
export function notify(text: string, tone: Notice["tone"] = "info", action?: Notice["action"], ttlMs = tone === "info" ? 5000 : 0): number {
  const id = ++noticeSeq;
  notices.value = [...notices.value, { id, text, tone, action }];
  if (ttlMs) setTimeout(() => dismiss(id), ttlMs);
  return id;
}
export function dismiss(id: number): void {
  notices.value = notices.value.filter((n) => n.id !== id);
}


export const canEdit = computed(() => !show.value.lock && doc.value !== null);
